import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { useSeo } from "@/seo";
import { SetStat } from "./SetIndex";
import CardRowEditor from "@/components/CardRowEditor";
import { OwnToggle, ProgressBar } from "@/components/OwnToggle";
import { ShareProgressSheet } from "@/components/ShareProgressSheet";
import { Share2 } from "lucide-react";

const DASH = "—";
const val = (v) => (v === null || v === undefined || v === "" ? DASH : v);
const PAGE = 60;

function ChaseRow({ r, t, locked }) {
  return (
    <tr className={`border-b border-[var(--wl-line)]/60 ${locked ? "wl-blur select-none" : "hover:bg-[var(--wl-panel2)]"}`} data-testid={locked ? "chase-row-locked" : `chase-row-${r.player_id}`} aria-hidden={locked || undefined}>
      <td className="py-1.5 pr-2 text-[var(--wl-muted)]">{r.rank}</td>
      <td className="pr-2">
        {locked ? <span className="text-[var(--wl-text)]">{r.player_name}</span>
          : <Link to={`/players/${r.player_id}`} className="font-bold text-[var(--wl-text)] hover:text-[var(--wl-accent)]" data-testid={`chase-player-link-${r.player_id}`}>{r.player_name}</Link>}
        <span className="ml-2 hidden text-[var(--wl-muted)] sm:inline">{val(r.club)}</span>
      </td>
      <td className="pr-2 text-right font-bold text-[var(--wl-accent)]" data-testid={locked ? undefined : `chase-hype-${r.player_id}`}>{val(r.hype)}</td>
      <td className="hidden pr-2 text-right text-[var(--wl-muted)] md:table-cell">{r.delta_7d === null || r.delta_7d === undefined ? DASH : `${r.delta_7d > 0 ? "+" : ""}${r.delta_7d}`}</td>
      <td className="hidden pr-2 text-[var(--wl-muted)] sm:table-cell">{r.reason?.key ? t(`reasons_short.${r.reason.key}`, r.reason.params || {}) : DASH}</td>
      <td className="pr-2 text-right">
        {(r.cards || []).map((c) => (
          <span key={c.card_id} className="ml-1 inline-flex items-center gap-1 whitespace-nowrap">
            {locked ? <span>#{c.card_number}</span> : <Link to={`/cards/${c.card_id}`} className="text-[var(--wl-text)] hover:text-[var(--wl-accent)]" data-testid={`chase-card-link-${c.card_id}`}>#{val(c.card_number)}</Link>}
            {c.is_rookie && <span className="wl-rc-badge">RC</span>}
          </span>
        ))}
      </td>
    </tr>
  );
}

const GHOST = ["Lorem Ipsumson", "Dolor Sitamet", "Consec Tetur", "Adipis Elit"];

// Manual RC override — admins only. Three states, "unknown" always reachable so a wrong
// manual flag can be undone. Never rendered for a visitor.
const RC_STATES = [{ v: true, k: "rc_yes", id: "true" }, { v: false, k: "rc_no", id: "false" }, { v: null, k: "rc_unknown", id: "unknown" }];

function RcCell({ r, onSet, busy, t }) {
  const state = r.rc_state === undefined ? null : r.rc_state;
  return (
    <span className="ml-2 hidden items-center gap-1 sm:inline-flex" data-testid={`rc-control-${r.card_id}`}>
      {RC_STATES.map(({ v, k, id }) => (
        <button key={id} type="button" disabled={busy} onClick={() => onSet(v, [r.card_id])} className="wl-chip" data-active={state === v}
          data-testid={`rc-set-${id}-${r.card_id}`}>{t(`sets.${k}`)}</button>
      ))}
      <span className="wl-mono text-[8.5px] uppercase tracking-[.04em] text-[var(--wl-muted)]" data-testid={`rc-source-${r.card_id}`}>
        {t(r.rc_source === "admin" ? "sets.rc_src_admin" : "sets.rc_src_publisher")}
        {r.publisher_code ? ` · ${r.publisher_code}` : ""}
      </span>
    </span>
  );
}

function Chase({ rows, total, locked, premium, sort, onSort, t }) {
  if (!total) {
    return (
      <section className="wl-panel mt-4 p-3" data-testid="rookies-to-chase">
        <h2 className="font-display text-base font-bold uppercase tracking-tight text-[var(--wl-text)] md:text-lg">{t("sets.chase")}</h2>
        <p className="wl-mono mt-1 text-[10px] uppercase tracking-[.04em] text-[var(--wl-muted)]" data-testid="chase-empty-state">{t("sets.chase_empty")}</p>
      </section>
    );
  }
  // Locked entries: real layout, blurred, fabricated placeholder values — the API never sent the real ones.
  const ghosts = Array.from({ length: Math.min(locked, 4) }, (_, i) => ({ rank: rows.length + i + 1, player_name: GHOST[i], club: "—", hype: 40 - i * 7, delta_7d: null, reason: null, cards: [{ card_id: `g${i}`, card_number: `${100 + i * 37}` }] }));
  return (
    <section className="wl-panel mt-4 p-3" data-testid="rookies-to-chase">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-display text-base font-bold uppercase tracking-tight text-[var(--wl-text)] md:text-lg">{t("sets.chase")}</h2>
        <span className="wl-mono text-[9px] uppercase tracking-[.04em] text-[var(--wl-muted)]" data-testid="chase-live-note">{t("sets.chase_live")}</span>
        {premium && (
          <div className="ml-auto flex gap-1" data-testid="chase-sort">
            {["hype", "delta"].map((s) => (
              <button key={s} type="button" className="wl-chip" data-active={sort === s} onClick={() => onSort(s)} data-testid={`chase-sort-${s}`}>{s === "hype" ? "HYPE" : "Δ7"}</button>
            ))}
          </div>
        )}
      </div>
      <table className="wl-mono mt-2 w-full text-[10.5px]">
        <thead className="text-[9px] uppercase tracking-[.06em] text-[var(--wl-muted)]">
          <tr className="border-b border-[var(--wl-line)] text-left"><th className="py-1 pr-2">#</th><th className="pr-2">{t("sets.player")}</th><th className="pr-2 text-right">HYPE</th><th className="hidden pr-2 text-right md:table-cell">Δ7</th><th className="hidden pr-2 sm:table-cell">{t("sets.reason")}</th><th className="pr-2 text-right">{t("sets.cards")}</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => <ChaseRow key={r.player_id} r={r} t={t} />)}
          {!premium && ghosts.map((g) => <ChaseRow key={g.rank} r={g} t={t} locked />)}
        </tbody>
      </table>
      {!premium && locked > 0 && (
        <Link to="/pricing" className="wl-mono mt-2 inline-flex items-center gap-1.5 text-[9.5px] uppercase tracking-[.06em] text-[var(--wl-accent)] hover:underline" data-testid="chase-locked-cta">
          {t("sets.chase_locked", { n: locked })}
        </Link>
      )}
    </section>
  );
}

export default function SetDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [list, setList] = useState({ rows: [], total: 0, teams: [] });
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState(params.get("q") || "");
  const { me } = useAuth();
  const [editing, setEditing] = useState(null);
  const [chaseSort, setChaseSort] = useState("hype");
  const [shareOpen, setShareOpen] = useState(false);
  const isAdmin = me?.role === "admin";
  const premium = !!me && (me.premium_access);
  const applyEdit = (card) => {
    setList((l) => ({ ...l, rows: l.rows.map((r) => (r.card_id === card.id ? { ...r, player_name: card.player_name_raw, team: card.team_raw } : r)) }));
    setEditing(null);
  };
  const subset = params.get("subset") || "";
  const team = params.get("team") || "";
  const rc = params.get("rc") === "1";
  const own = params.get("own") || "all";
  const [coll, setColl] = useState(null);
  // Progress recomputed locally on each tick: no refetch, the bar moves with the thumb.
  const setOwned = (row, next) => {
    const was = row.owned_qty > 0, is = next > 0;
    setList((l) => ({ ...l, rows: l.rows.map((r) => (r.card_id === row.card_id ? { ...r, owned_qty: next } : r)) }));
    if (was === is || !coll) return;
    const d = is ? 1 : -1;
    setColl((c) => {
      const sub = c.subsets[row.subset] || { owned: 0, total: undefined };
      const so = sub.owned + d, o = c.owned + d;
      return { ...c, owned: o, pct: c.total ? Math.round((100 * o) / c.total) : 0,
        subsets: { ...c.subsets, [row.subset]: { ...sub, owned: so, pct: sub.total ? Math.round((100 * so) / sub.total) : 0 } } };
    });
  };
  const [sel, setSel] = useState(() => new Set());
  const [rcBusy, setRcBusy] = useState(false);
  const toggleSel = (id) => setSel((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const s = detail?.set;
  useSeo(s ? `${s.set_name} — ${t("sets.seo_checklist")}` : t("sets.checklist"),
    s ? t(s.rc_declared === false ? "sets.detail_desc_no_rc" : "sets.detail_desc", { name: s.set_name, season: s.season_short, n: s.total_cards, rc: s.rc_cards }) : "",
    s ? `/sets/${s.slug}` : null);

  useEffect(() => {
    api.get(`/sets/${slug}`, { params: { sort: chaseSort, lang } }).then((r) => {
      setDetail(r.data);
      setColl(r.data.collection || null);
      // Landed on a corrected slug: move the address to the current one without adding a
      // history entry, so a shared old link still works but stops being propagated.
      if (r.data.canonical_slug && r.data.canonical_slug !== slug) {
        navigate(`/cards/${r.data.canonical_slug}${window.location.search}`, { replace: true });
      }
    }).catch((e) => setError(e?.response?.status === 404 ? t("sets.not_found") : t("common.error")));
  }, [slug, t, chaseSort, lang, me]);

  const fetchPage = useCallback((skip) => {
    const p = { skip, limit: PAGE };
    if (subset) p.subset = subset;
    if (team) p.team = team;
    if (rc) p.rc = true;
    if (own !== "all") p.own = own;
    if (params.get("q")) p.q = params.get("q");
    return api.get(`/sets/${slug}/cards`, { params: p }).then((r) => r.data);
  }, [slug, subset, team, rc, own, params, me]);

  // Une requête interrompue n'est pas une liste de cartes vide : on réessaie une fois.
  useEffect(() => {
    let alive = true;
    const ask = (tries) => {
      fetchPage(0)
        .then((d) => { if (alive) setList(d); })
        .catch(() => { if (alive && tries > 0) setTimeout(() => ask(tries - 1), 1500); });
    };
    ask(2);
    return () => { alive = false; };
  }, [fetchPage]);

  const setFilter = (k, v) => {
    const next = Object.fromEntries(params.entries());
    if (v) next[k] = v; else delete next[k];
    setParams(next);
  };
  const loadMore = () => {
    setLoadingMore(true);
    fetchPage(list.rows.length).then((d) => setList((l) => ({ ...d, rows: [...l.rows, ...d.rows] }))).finally(() => setLoadingMore(false));
  };

  // wholeFilter = every card of the CURRENT filter (734 rows), not just the page.
  const applyRc = async (value, ids, wholeFilter = false) => {
    const n = wholeFilter ? list.total : ids.length;
    if (!n || !window.confirm(t("sets.rc_confirm", { n }))) return;
    setRcBusy(true);
    const patch = (r) => ({ ...r, rc_state: value, is_rookie: value === true, rc_source: "admin" });
    try {
      const body = wholeFilter
        ? { value, set_slug: slug, subset: subset || null, team: team || null, q: params.get("q") || null, rc_only: rc }
        : { value, card_ids: ids };
      await api.post("/admin/cards/rc-flag", body);
      const touched = new Set(ids);
      setList((l) => ({ ...l, rows: l.rows.map((r) => (wholeFilter || touched.has(r.card_id) ? patch(r) : r)) }));
      setSel(new Set());
    } finally { setRcBusy(false); }
  };

  if (error) return <p className="wl-mono pt-16 text-center text-[11px] uppercase text-red-400" data-testid="set-error">{error}</p>;
  if (!detail) return <p className="wl-mono pt-16 text-center text-[11px] uppercase text-[var(--wl-muted)]">{t("common.loading")}</p>;
  const comp = s.completeness;
  const markets = detail.marketplaces || [];

  return (
    <div className="wl-scope -mx-4 -mb-16 min-h-[80vh] px-4 pt-6 pb-10 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8" data-testid="set-detail-page">
      <Link to="/cards" className="wl-mono text-[9.5px] uppercase tracking-[.06em] text-[var(--wl-muted)] hover:text-[var(--wl-accent)]" data-testid="set-back-link">← {t("sets.index_h1")}</Link>
      <header className="mt-2 border-b border-[var(--wl-line)] pb-4">
        <div className="wl-mono text-[9.5px] uppercase tracking-[.06em] text-[var(--wl-muted)]">{s.manufacturer} · {s.season_short || s.season}</div>
        <h1 className="font-display mt-1 text-2xl font-bold uppercase tracking-tight text-[var(--wl-text)] sm:text-3xl" data-testid="set-title">{s.set_name}</h1>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SetStat label={t("sets.cards")} value={s.total_cards} testid="set-total-cards" />
          <SetStat label="RC" value={s.rc_declared === false ? "n/r" : s.rc_cards} testid="set-rc-cards" />
          <SetStat label={t("sets.subsets")} value={s.subsets} testid="set-subsets" />
          <SetStat label={t("sets.season")} value={s.season} />
        </div>
        <p className="wl-mono mt-3 text-[9.5px] uppercase tracking-[.04em] text-[var(--wl-muted)]" data-testid="set-completeness">
          {comp ? (comp.verified ? t("sets.completeness_ok", { sections: comp.sections, rows: comp.rows }) : t("sets.completeness_fail")) : t("sets.completeness_unknown")}
        </p>
        {s.rc_declared === false && (
          <p className="wl-mono mt-1 text-[9.5px] uppercase tracking-[.04em] text-[var(--wl-muted)]" data-testid="set-rc-not-declared">
            {t("sets.rc_not_declared")}
          </p>
        )}
      </header>

      {coll && (
        <section className="wl-panel mt-4 p-3" data-testid="collection-progress">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-base font-bold uppercase tracking-tight text-[var(--wl-text)] md:text-lg">{t("sets.coll_progress")}</h2>
            <span className="wl-mono ml-auto text-[12px] font-bold tabular-nums text-[var(--wl-accent)]" data-testid="collection-progress-count">{t("sets.coll_owned_of", { owned: coll.owned, total: coll.total, pct: coll.pct })}</span>
          </div>
          <div className="mt-2"><ProgressBar owned={coll.owned} total={coll.total} pct={coll.pct} testid="collection-progress-bar" /></div>
          {premium && coll.owned > 0 && (
            <button type="button" onClick={() => setShareOpen(true)} data-testid="share-progress-open"
              className="wl-mono mt-3 inline-flex items-center gap-1.5 rounded-md border border-[var(--wl-accent)]/50 bg-[var(--wl-accent)]/10 px-2.5 py-1.5 text-[10px] uppercase tracking-[.06em] text-[var(--wl-accent)] transition-colors hover:bg-[var(--wl-accent)]/20">
              <Share2 className="h-3.5 w-3.5" /> {t("sets.share_progress")}
            </button>
          )}
          {coll.locked && (
            <Link to="/pricing" className="wl-mono mt-2 inline-block text-[9.5px] uppercase tracking-[.06em] text-[var(--wl-accent)] hover:underline" data-testid="collection-locked-cta">{t("sets.coll_locked")}</Link>
          )}
          {!me && <p className="wl-mono mt-2 text-[9.5px] uppercase tracking-[.04em] text-[var(--wl-muted)]" data-testid="collection-anon-hint">{t("sets.coll_sign_in")}</p>}
        </section>
      )}
      {coll && premium && s && (
        <ShareProgressSheet slug={s.slug || slug} setName={s.set_name} pct={coll.pct} open={shareOpen} onClose={() => setShareOpen(false)} />
      )}

      <Chase rows={detail.rookies_to_chase} total={detail.rookies_to_chase_total} locked={detail.rookies_to_chase_locked || 0} premium={premium} sort={chaseSort} onSort={setChaseSort} t={t} />

      <section className="mt-4" data-testid="subset-breakdown">
        <h2 className="font-display text-base font-bold uppercase tracking-tight text-[var(--wl-text)] md:text-lg">{t("sets.subsets")}</h2>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <button type="button" className="wl-chip" data-active={!subset} onClick={() => setFilter("subset", "")} data-testid="subset-chip-all">{t("sets.all")}<b>{s.total_cards}</b></button>
          {detail.subsets.map((g) => {
            const sp = coll?.subsets?.[g.name];
            return (
              <button key={g.name} type="button" className="wl-chip flex-col !items-start" data-active={subset === g.name} onClick={() => setFilter("subset", g.name)} data-testid={`subset-chip-${g.name.replace(/\s+/g, "-")}`}>
                <span>{g.name}<b>{sp && sp.owned > 0 ? `${sp.owned}/${g.cards}` : g.cards}</b></span>
                {sp && sp.owned > 0 && <span className="mt-1 block w-full min-w-[60px]"><ProgressBar owned={sp.owned} total={sp.total} pct={sp.pct} compact testid={`subset-progress-${g.name.replace(/\s+/g, "-")}`} /></span>}
              </button>
            );
          })}
        </div>
      </section>

      <section className="mt-5" data-testid="set-checklist">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-display text-base font-bold uppercase tracking-tight text-[var(--wl-text)] md:text-lg">{t("sets.checklist")}</h2>
          <form onSubmit={(e) => { e.preventDefault(); setFilter("q", q.trim()); }} className="ml-auto flex">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("sets.search_cards_ph")} className="wl-mono w-48 border border-[var(--wl-line)] bg-[var(--wl-panel)] px-2 py-1 text-[10.5px] text-[var(--wl-text)] outline-none focus:border-[var(--wl-accent)]" data-testid="checklist-search-input" />
            <button type="submit" className="wl-mono border border-l-0 border-[var(--wl-line)] px-2 text-[9.5px] uppercase text-[var(--wl-muted)] hover:border-[var(--wl-accent)]" data-testid="checklist-search-submit">{t("sets.search")}</button>
          </form>
          <select value={team} onChange={(e) => setFilter("team", e.target.value)} className="wl-mono border border-[var(--wl-line)] bg-[var(--wl-panel)] px-2 py-1 text-[10px] text-[var(--wl-text)]" data-testid="checklist-filter-team">
            <option value="">{t("sets.team")} — {t("sets.any")}</option>
            {list.teams.map((tm) => <option key={tm} value={tm}>{tm}</option>)}
          </select>
          <label className="wl-mono flex items-center gap-1 text-[9.5px] uppercase tracking-[.04em] text-[var(--wl-muted)]">
            <input type="checkbox" checked={rc} onChange={(e) => setFilter("rc", e.target.checked ? "1" : "")} data-testid="checklist-filter-rc" /> {t("sets.rc_only")}
          </label>
          <span className="wl-mono text-[9px] uppercase tracking-[.04em] text-[var(--wl-muted)]" data-testid="checklist-total">{list.total} · {t("sets.cards")}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="collection-filter">
          {["all", "owned", "missing"].map((k) => (
            <button key={k} type="button" className="wl-chip min-h-[32px]" data-active={own === k} onClick={() => setFilter("own", k === "all" ? "" : k)} data-testid={`collection-filter-${k}`}>{t(`sets.coll_${k}`)}</button>
          ))}
        </div>

        {isAdmin && (
          <div className="mt-2 hidden flex-wrap items-center gap-2 border sm:flex border-[var(--wl-accent)]/40 bg-[var(--wl-accent)]/5 px-2 py-1.5" data-testid="rc-bulk-bar">
            <span className="wl-mono text-[9.5px] uppercase tracking-[.06em] text-[var(--wl-accent)]" data-testid="rc-bulk-count">{t("sets.rc_selected", { n: sel.size })}</span>
            {RC_STATES.map(({ v, k, id }) => (
              <button key={id} type="button" disabled={rcBusy || sel.size === 0} onClick={() => applyRc(v, [...sel])} className="wl-chip disabled:opacity-40" data-testid={`rc-bulk-${id}`}>{t(`sets.${k}`)}</button>
            ))}
            <span className="wl-mono text-[9px] uppercase text-[var(--wl-muted)]">|</span>
            {RC_STATES.map(({ v, k, id }) => (
              <button key={`f-${id}`} type="button" disabled={rcBusy} onClick={() => applyRc(v, [], true)} className="wl-chip disabled:opacity-40" data-testid={`rc-filter-${id}`}>
                {t("sets.rc_apply_filter", { state: t(`sets.${k}`), n: list.total })}
              </button>
            ))}
          </div>
        )}

        <table className="wl-mono mt-2 w-full text-[10.5px]" data-testid="checklist-table">
          <thead className="text-[9px] uppercase tracking-[.06em] text-[var(--wl-muted)]">
            <tr className="border-b border-[var(--wl-line)] text-left">{isAdmin && <th className="hidden py-1 pr-2 sm:table-cell"><input type="checkbox" checked={list.rows.length > 0 && sel.size >= list.rows.length} onChange={(e) => setSel(e.target.checked ? new Set(list.rows.map((r) => r.card_id)) : new Set())} data-testid="rc-select-page" /></th>}<th className="w-8 py-1 pr-1" aria-label={t("sets.coll_own_label")} /><th className="py-1 pr-2">#</th><th className="pr-2">{t("sets.player")}</th><th className="hidden pr-2 sm:table-cell">{t("sets.team")}</th><th className="pr-2">{t("sets.subset")} · RC</th><th className="pr-2 text-right">{t("sets.listings")}</th></tr>
          </thead>
          <tbody>
            {list.rows.map((r) => (
              <tr key={r.card_id} className="border-b border-[var(--wl-line)]/60 hover:bg-[var(--wl-panel2)]" data-owned={r.owned_qty > 0} data-testid={`checklist-row-${r.card_id}`}>
                {isAdmin && <td className="hidden py-1 pr-2 sm:table-cell"><input type="checkbox" checked={sel.has(r.card_id)} onChange={() => toggleSel(r.card_id)} data-testid={`rc-select-${r.card_id}`} /></td>}
                <td className="py-1 pr-1"><OwnToggle cardId={r.card_id} qty={r.owned_qty || 0} onChange={(n) => setOwned(r, n)} locked={!!coll?.locked} /></td>
                <td className="py-1 pr-2 text-[var(--wl-text)]">{val(r.card_number)}</td>
                <td className="pr-2">
                  {editing === r.card_id ? <CardRowEditor row={r} onSaved={applyEdit} onCancel={() => setEditing(null)} />
                    : r.player_id ? <Link to={`/players/${r.player_id}`} className="text-[var(--wl-text)] hover:text-[var(--wl-accent)]" data-testid={`checklist-player-link-${r.card_id}`}>{r.player_name}</Link> : <span className="text-[var(--wl-text)]">{r.player_name}</span>}
                  {isAdmin && editing !== r.card_id && (
                    <button type="button" onClick={() => setEditing(r.card_id)} className="wl-mono ml-2 text-[9px] uppercase text-[var(--wl-muted)] hover:text-[var(--wl-accent)]" data-testid={`card-edit-btn-${r.card_id}`}>{t("sets.edit")}</button>
                  )}
                </td>
                <td className="hidden pr-2 text-[var(--wl-muted)] sm:table-cell">{val(r.team)}</td>
                <td className="pr-2">
                  <span className="inline-flex items-center gap-1">
                    <span className="wl-subset-badge" data-testid={`checklist-subset-${r.card_id}`}>{r.subset}</span>
                    {r.is_rookie && <span className="wl-rc-badge" data-testid={`checklist-rc-${r.card_id}`}>RC</span>}
                    {isAdmin && <RcCell r={r} onSet={applyRc} busy={rcBusy} t={t} />}
                  </span>
                </td>
                <td className="pr-2 text-right">
                  {markets.length ? <a href={markets[0].url.replace("{QUERY}", r.market_query)} target="_blank" rel="noopener noreferrer nofollow" className="text-[var(--wl-muted)] hover:text-[var(--wl-accent)]" data-testid={`checklist-market-${r.card_id}`}><span className="hidden sm:inline">{t("sets.check_listings")} </span>↗</a> : DASH}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.rows.length < list.total && (
          <button type="button" onClick={loadMore} disabled={loadingMore} className="wl-mono mt-3 border border-[var(--wl-line)] px-4 py-2 text-[10px] uppercase tracking-[.06em] text-[var(--wl-text)] hover:border-[var(--wl-accent)] disabled:opacity-50" data-testid="checklist-load-more">
            {loadingMore ? t("common.loading") : `${t("wl.load_more")} · ${list.rows.length}/${list.total}`}
          </button>
        )}
      </section>
    </div>
  );
}
